import { useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  DataTable,
  InputText,
  NumberInput,
  Select,
  type ColumnDef,
  type SelectOption,
  SectionCard,
} from "@/deps/ui-library";
import { api } from "@/api";
import { useBankAccounts, useRecordReceipt } from "@/api/queries";
import type { InvoiceSummary, Party, ReceiptMethod } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { useStatementLineStart } from "@/features/banking/reconciliation/fromStatementLine";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { RECEIPT_METHODS } from "@/lib/receiptMethods";
import { byEarliestDue, earliestDueFirst } from "@/lib/settlement";

/**
 * How the amount boxes format what is typed. Grouping is off because the screen parses the string
 * (`paisa` below): RDS formats on blur, and "2,500" would parse as nothing at all.
 */
const MONEY = { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 };

const paisa = (value: string) => Math.round(Number(value || "0") * 100);

/** An open invoice as the allocation table shows it: with what is being applied to it. */
type OpenRow = InvoiceSummary & { applying: string };

/** The value an RDS single `Select` hands back, as the plain string this screen holds. */
function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/**
 * The allocation table's columns, built once per screen. The table rebuilds its cells when the
 * columns change, and a rebuilt cell is a new <input> that has lost the focus and the caret
 * mid-number — so what changes as it is typed travels in the rows instead.
 */
function openInvoiceColumns(
  setApplying: (invoiceId: string, value: string) => void,
): Array<ColumnDef<OpenRow>> {
  return [
    {
      id: "number",
      header: "Invoice",
      cell: ({ row: { original: invoice } }) => <span className="num">{invoice.number}</span>,
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row: { original: invoice } }) => (
        <span className="text-muted-foreground text-xs">
          {invoice.due ? formatDate(invoice.due) : ""}
        </span>
      ),
    },
    {
      id: "outstanding",
      header: "Outstanding",
      meta: { textAlign: "right" },
      cell: ({ row: { original: invoice } }) => (
        <span className="num">{formatAmount(invoice.balance)}</span>
      ),
    },
    {
      id: "apply",
      header: "Apply",
      size: 140,
      meta: { textAlign: "right" },
      cell: ({ row: { original: invoice } }) => {
        // Refused as it is typed, not on Save: the box turns red and the note below names it.
        const over = paisa(invoice.applying) > paisa(invoice.balance);
        return (
          // RDS `NumberInput` now forwards `aria-label` to its `<input>`, so a cell's box can say
          // which invoice it belongs to — which is the only reason this was a plain text box.
          <NumberInput
            aria-label={`Amount to apply to ${invoice.number}`}
            invalid={over}
            value={invoice.applying}
            onValueChange={(details) => setApplying(invoice.id, details.value)}
            formatOptions={MONEY}
            config={{ hideTriggers: true }}
            className="ml-auto w-28"
            inputProps={{ className: "num pr-3 text-right" }}
          />
        );
      },
    },
  ];
}

/**
 * Record a receipt against a customer's open invoices.
 *
 * Two rules are enforced here rather than discovered on Save:
 *
 * **Nothing over-settles.** An invoice paid twice is a credit nobody asked for, so an amount larger
 * than what is outstanding is refused as it is typed.
 *
 * **Whatever is left over is an advance, and it says so.** What arrives settles the earliest due
 * invoice first, on the screen where it can be seen (ADR-0035). The person can apply it to other
 * invoices instead, or clear it and hold it all as an advance.
 */
export function RecordReceiptPage() {
  const navigate = useNavigate();
  const fromLine = useStatementLineStart();
  const [customer, setCustomer] = useState<Party | null>(null);
  const [open, setOpen] = useState<InvoiceSummary[]>([]);
  const [date, setDate] = useState(fromLine.start?.date ?? today());
  const [method, setMethod] = useState<ReceiptMethod>("Bank transfer");
  const [received, setReceived] = useState(fromLine.start?.amount ?? "");
  // Null until someone changes an application: until then, what arrives settles earliest due first.
  const [typed, setTyped] = useState<Record<string, string> | null>(null);
  const [withheld, setWithheld] = useState("");

  // The account the money landed in, chosen rather than assumed. This screen used to send
  // `bankAccountId: "bank_1"`, which was not the id of any bank account in the product.
  const { data: bankAccounts } = useBankAccounts();
  const [bankAccountId, setBankAccountId] = useState(fromLine.start?.account ?? "");
  const account = bankAccounts?.find((a) => a.id === bankAccountId) ?? bankAccounts?.[0];

  const record = useRecordReceipt();

  const chooseCustomer = (party: Party | null) => {
    setCustomer(party);
    setTyped(null);
    if (party) void api.receipts.openInvoicesFor(party.id).then(setOpen);
    else setOpen([]);
  };

  usePartyFromAddress("customer", chooseCustomer);

  const receivedTotal = paisa(received);
  const withheldTotal = paisa(withheld);
  const applied = typed ?? earliestDueFirst(open, receivedTotal + withheldTotal);
  const appliedTotal = Object.values(applied).reduce((sum, value) => sum + paisa(value), 0);

  // RULE 4.2: what the customer discharged is the cash plus anything they withheld. The debt falls
  // by the full amount; only the cash is smaller.
  const settled = receivedTotal + withheldTotal;
  const advance = settled - appliedTotal;

  const overApplied = open.filter(
    (invoice) => paisa(applied[invoice.id] ?? "0") > paisa(invoice.balance),
  );
  const overSettled = appliedTotal > settled;
  // Tax withheld is withheld on an invoice, and the certificate names it (RULE 4.3).
  const overWithheld = withheldTotal > appliedTotal;
  // The statement line is the cash that arrived, and a match has to agree exactly.
  const offTheLine = fromLine.start !== null && receivedTotal !== paisa(fromLine.start.amount);

  // A receipt settles invoices, is held as an advance, or both. Requiring an application — which
  // this did — made the screen's own sentence about advances false, and left no way to record money
  // that had actually arrived before there was an invoice for it.
  const canSave =
    customer !== null &&
    account !== undefined &&
    settled > 0 &&
    overApplied.length === 0 &&
    !overSettled &&
    !overWithheld &&
    !offTheLine &&
    !record.isPending;

  /**
   * The applications, with the withheld tax spread across them.
   *
   * The screen asks for one withheld figure because that is what a person knows: the customer kept
   * this much and paid it to the IRD. The wire carries it per invoice, because that is what a TDS
   * certificate is issued against. Apportioning by what each invoice was applied is the only split
   * that does not need a second question, and the largest remainder goes to the last line so the
   * parts add up to the whole rather than to a paisa less.
   *
   * It used to send `tdsWithheld: "0.00"` for every application while showing the withheld amount
   * in the arithmetic above — so the applications came to more than the receipt, and nothing
   * downstream could reconcile the two.
   */
  const withholdingApportioned = () => {
    const entries = Object.entries(applied).filter(([, value]) => paisa(value) > 0);
    let left = withheldTotal;

    return entries.map(([invoiceId, value], index) => {
      const share =
        index === entries.length - 1
          ? left
          : Math.round((withheldTotal * paisa(value)) / appliedTotal);
      left -= share;

      return {
        invoiceId,
        applied: value,
        tdsWithheld: (share / 100).toFixed(2),
      };
    });
  };

  // The columns are built once, so a cell reads what is applied now through this. RDS also reports
  // the value it was handed, and only a figure that differs is someone changing it.
  const appliedNow = useRef(applied);
  appliedNow.current = applied;
  const columns = useMemo(
    () =>
      openInvoiceColumns((invoiceId, value) => {
        if (paisa(value) === paisa(appliedNow.current[invoiceId] ?? "")) return;
        setTyped({ ...appliedNow.current, [invoiceId]: value });
      }),
    [],
  );
  const rows: OpenRow[] = byEarliestDue(open).map((invoice) => ({
    ...invoice,
    applying: applied[invoice.id] ?? "",
  }));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Record receipt"
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() =>
                fromLine.start ? fromLine.back() : void navigate({ to: "/sales/receipts" })
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={() => {
                record.mutate(
                  {
                    customerId: customer!.id,
                    date,
                    bankAccountId: account!.id,
                    method,
                    amount: (receivedTotal / 100).toFixed(2),
                    applications: withholdingApportioned(),
                  },
                  {
                    onSuccess: (receipt) => {
                      if (fromLine.start) void fromLine.finish(receipt.journalId);
                      else void navigate({ to: "/sales/receipts" });
                    },
                  },
                );
              }}
            >
              {record.isPending ? "Recording" : "Record receipt"}
            </Button>
          </span>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(360px,1fr)_minmax(380px,420px)] lg:items-start">
        <SectionCard title="Receipt">
          <div className="flex flex-col gap-3">
            <PartyCombobox
              label="Customer"
              required
              value={customer}
              onChange={chooseCustomer}
              search={(q) => api.parties.search("customer", q)}
            />

            <InputText
              label="Received on"
              required
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />

            <Select
              label="Method"
              required
              options={RECEIPT_METHODS.map((option) => ({ value: option, label: option }))}
              value={method}
              onChange={(details) => {
                const picked = chosen(details);
                if (picked) setMethod(picked as ReceiptMethod);
              }}
              showClearButton={false}
            />

            <Select
              label="Into"
              required
              options={(bankAccounts ?? []).map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              value={account?.id ?? ""}
              onChange={(details) => setBankAccountId(chosen(details))}
              showClearButton={false}
            />

            <NumberInput
              label="Amount received"
              required
              value={received}
              onValueChange={(details) => setReceived(details.value)}
              formatOptions={MONEY}
              config={{ hideTriggers: true }}
            />

            <NumberInput
              label="Tax withheld at source"
              value={withheld}
              onValueChange={(details) => setWithheld(details.value)}
              formatOptions={MONEY}
              config={{ hideTriggers: true }}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Open invoices"
          className="lg:max-h-[640px] lg:overflow-y-auto"
          actions={
            open.length > 0 ? (
              <span className="flex gap-1">
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  disabled={typed === null}
                  onClick={() => setTyped(null)}
                >
                  Apply earliest due first
                </Button>
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  onClick={() => setTyped({})}
                >
                  Clear
                </Button>
              </span>
            ) : null
          }
          flush
        >
          {customer === null ? (
            <p className="text-fg-muted p-4 text-sm">Choose a customer to see what they owe.</p>
          ) : open.length === 0 ? (
            <p className="text-fg-muted p-4 text-sm">
              {customer.name} has nothing outstanding. Anything received will be held as an advance.
            </p>
          ) : (
            <DataTable
              aria-label={`Open invoices for ${customer.name}`}
              size="sm"
              enableSorting={false}
              columns={columns}
              data={rows}
              getRowId={(invoice) => invoice.id}
              pagination={{ mode: "manual", show: false }}
            />
          )}

          {/* The consequences, stated as they are typed rather than as a rejection after Save. */}
          <div className="border-line flex flex-col gap-1 border-t px-3 py-2 text-xs">
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">Settled by this receipt</span>
              <span className="num text-fg">{formatAmount((settled / 100).toFixed(2))}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">Applied to invoices</span>
              <span className="num text-fg">{formatAmount((appliedTotal / 100).toFixed(2))}</span>
            </div>
            {advance > 0 ? (
              <div className="flex items-baseline justify-between">
                <span className="text-warn-text">Held as a customer advance</span>
                <span className="num text-warn-text">
                  {formatAmount((advance / 100).toFixed(2))}
                </span>
              </div>
            ) : null}

            {overApplied.length > 0 ? (
              <p role="alert" className="text-bad-text">
                {overApplied.map((i) => i.number).join(", ")} would be over-settled. An invoice paid
                twice is a credit nobody asked for.
              </p>
            ) : null}

            {record.isError ? (
              <p role="alert" className="text-bad-text">
                {record.error instanceof Error
                  ? record.error.message
                  : "That receipt could not be recorded."}
              </p>
            ) : null}

            {offTheLine ? (
              <p role="alert" className="text-bad-text">
                The amount received has to be {formatAmount(fromLine.start!.amount)}, the amount on
                the statement line, for the two to match.
              </p>
            ) : null}

            {overWithheld ? (
              <p role="alert" className="text-bad-text">
                Tax withheld is more than this receipt applies to invoices. Apply it to the invoices
                the tax was withheld on.
              </p>
            ) : null}

            {overSettled ? (
              <p role="alert" className="text-bad-text">
                You have applied more than this receipt settles. Reduce the amounts, or record what
                actually arrived.
              </p>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
